import React, { useState, useEffect } from "react";
import { Setting } from "@/api/entities";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, Server, RefreshCw, CheckCircle, XCircle, Save, Bell, Bot } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { invokeFunctions } from "@/api/integrations";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEntorno } from "@/lib/EntornoContext";
import { Badge } from "@/components/ui/badge";

export default function ConfiguracionCorreo() {
  const { entorno, isPruebas } = useEntorno();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  
  // SMTP Config
  const [smtpHost, setSmtpHost] = useState("");
  const [smtpPort, setSmtpPort] = useState("465");
  const [smtpUser, setSmtpUser] = useState("");
  const [smtpPassword, setSmtpPassword] = useState("");
  
  // IMAP Config
  const [imapHost, setImapHost] = useState("");
  const [imapPort, setImapPort] = useState("993");
  const [imapUser, setImapUser] = useState("");
  const [imapPassword, setImapPassword] = useState("");
  
  // Discord Config
  const [discordWebhook, setDiscordWebhook] = useState("");
  
  // Gemini API Key
  const [geminiApiKey, setGeminiApiKey] = useState("");

  // Load existing settings
  const { data: settings = [] } = useQuery({
    queryKey: ['settings'],
    queryFn: () => Setting.list(),
  });

  // Load settings into state
  useEffect(() => {
    const suffix = isPruebas ? '_test' : '';
    const smtpKey = 'smtp_config' + suffix;
    const imapKey = 'imap_config' + suffix;
    const discordKey = 'discord_webhook' + suffix;
    const geminiKey = 'gemini_api_key' + suffix;

    settings.forEach(s => {
      try {
        if (s.clave === smtpKey) {
          const config = JSON.parse(s.valor);
          setSmtpHost(config.host || "");
          setSmtpPort(config.port || "465");
          setSmtpUser(config.user || "");
          setSmtpPassword(config.pass || "");
        }
        if (s.clave === imapKey) {
          const config = JSON.parse(s.valor);
          setImapHost(config.host || "");
          setImapPort(config.port || "993");
          setImapUser(config.user || "");
          setImapPassword(config.pass || "");
        }
        if (s.clave === discordKey) {
          setDiscordWebhook(s.valor || "");
        }
        if (s.clave === geminiKey) {
          setGeminiApiKey(s.valor || "");
        }
      } catch (e) {}
    });

    // Reset fields if not found in current environment
    if (!settings.some(s => s.clave === smtpKey)) { setSmtpHost(""); setSmtpUser(""); setSmtpPassword(""); }
    if (!settings.some(s => s.clave === imapKey)) { setImapHost(""); setImapUser(""); setImapPassword(""); }
    if (!settings.some(s => s.clave === discordKey)) setDiscordWebhook("");
    if (!settings.some(s => s.clave === geminiKey)) setGeminiApiKey("");

  }, [settings, isPruebas]);

  // Save settings
  const handleSave = async () => {
    setIsSaving(true);
    const suffix = isPruebas ? '_test' : '';
    try {
      // SMTP Config
      const smtpKey = 'smtp_config' + suffix;
      const smtpSetting = settings.find(s => s.clave === smtpKey);
      const smtpConfig = JSON.stringify({ host: smtpHost, port: smtpPort, user: smtpUser, pass: smtpPassword });
      if (smtpSetting) {
        await Setting.update(smtpSetting.id, { valor: smtpConfig });
      } else {
        await Setting.create({ clave: smtpKey, valor: smtpConfig, descripcion: `Configuración SMTP (${isPruebas ? 'Pruebas' : 'Producción'})` });
      }

      // IMAP Config
      const imapKey = 'imap_config' + suffix;
      const imapSetting = settings.find(s => s.clave === imapKey);
      const imapConfig = JSON.stringify({ host: imapHost, port: imapPort, user: imapUser, pass: imapPassword });
      if (imapSetting) {
        await Setting.update(imapSetting.id, { valor: imapConfig });
      } else {
        await Setting.create({ clave: imapKey, valor: imapConfig, descripcion: `Configuración IMAP (${isPruebas ? 'Pruebas' : 'Producción'})` });
      }

      // Discord Webhook
      const discordKey = 'discord_webhook' + suffix;
      const discordSetting = settings.find(s => s.clave === discordKey);
      if (discordSetting) {
        await Setting.update(discordSetting.id, { valor: discordWebhook });
      } else {
        await Setting.create({ clave: discordKey, valor: discordWebhook, descripcion: `Discord Webhook URL (${isPruebas ? 'Pruebas' : 'Producción'})` });
      }

      // Gemini API Key
      const geminiKey = 'gemini_api_key' + suffix;
      const geminiSetting = settings.find(s => s.clave === geminiKey);
      if (geminiSetting) {
        await Setting.update(geminiSetting.id, { valor: geminiApiKey });
      } else {
        await Setting.create({ clave: geminiKey, valor: geminiApiKey, descripcion: `Gemini API Key (${isPruebas ? 'Pruebas' : 'Producción'})` });
      }

      queryClient.invalidateQueries({ queryKey: ['settings'] });
      toast({ title: "Configuración guardada", description: `Ajustes de ${isPruebas ? 'Pruebas' : 'Producción'} guardados correctamente` });
    } catch (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
    setIsSaving(false);
  };

  const handleSyncEmails = async () => {
    setIsSyncing(true);
    setSyncResult(null);
    
    try {
      const response = await invokeFunctions('fetchEmails', {});
      setSyncResult({ success: true, message: response.data?.message || "Emails sincronizados" });
      toast({ title: "Sincronización completada", description: response.data?.message });
    } catch (error) {
      const errorMsg = error.response?.data?.error || error.message;
      setSyncResult({ success: false, message: errorMsg });
      toast({ title: "Error en sincronización", description: errorMsg, variant: "destructive" });
    }
    
    setIsSyncing(false);
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-8 flex justify-between items-center">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Configuración de Correo</h1>
            <Badge variant={isPruebas ? "destructive" : "default"} className="text-lg">
              {isPruebas ? "MODO PRUEBAS" : "PRODUCCIÓN"}
            </Badge>
          </div>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Configura IMAP/SMTP, Discord y Gemini AI para el entorno de {isPruebas ? 'Pruebas' : 'Producción'}
          </p>
        </div>
        <Button onClick={handleSave} disabled={isSaving} className="bg-green-600 hover:bg-green-700">
          {isSaving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Guardar Todo
        </Button>
      </div>

      <div className="space-y-6">
        {/* SMTP */}
        <Card className="dark:bg-gray-900 dark:border-gray-800">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-gray-900 dark:text-white">
              <Mail className="w-5 h-5" />
              Configuración SMTP (Envío de Emails)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Servidor SMTP</Label>
                <Input value={smtpHost} onChange={(e) => setSmtpHost(e.target.value)} placeholder="mail.tudominio.com" />
              </div>
              <div>
                <Label>Puerto</Label>
                <Input value={smtpPort} onChange={(e) => setSmtpPort(e.target.value)} placeholder="465" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Usuario/Email</Label>
                <Input value={smtpUser} onChange={(e) => setSmtpUser(e.target.value)} placeholder="info@tudominio.com" />
              </div>
              <div>
                <Label>Contraseña</Label>
                <Input type="password" value={smtpPassword} onChange={(e) => setSmtpPassword(e.target.value)} placeholder="••••••••" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* IMAP */}
        <Card className="dark:bg-gray-900 dark:border-gray-800">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-gray-900 dark:text-white">
              <Server className="w-5 h-5" />
              Configuración IMAP (Recepción de Emails)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Servidor IMAP</Label>
                <Input value={imapHost} onChange={(e) => setImapHost(e.target.value)} placeholder="imap.tudominio.com" />
              </div>
              <div>
                <Label>Puerto</Label>
                <Input value={imapPort} onChange={(e) => setImapPort(e.target.value)} placeholder="993" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Usuario/Email</Label>
                <Input value={imapUser} onChange={(e) => setImapUser(e.target.value)} placeholder="info@tudominio.com" />
              </div>
              <div>
                <Label>Contraseña</Label>
                <Input type="password" value={imapPassword} onChange={(e) => setImapPassword(e.target.value)} placeholder="••••••••" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Discord */}
        <Card className="dark:bg-gray-900 dark:border-gray-800">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-gray-900 dark:text-white">
              <Bell className="w-5 h-5" />
              Discord Webhook (Notificaciones)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Label>URL del Webhook</Label>
            <Input 
              value={discordWebhook} 
              onChange={(e) => setDiscordWebhook(e.target.value)} 
              placeholder="https://discord.com/api/webhooks/..." 
            />
            <p className="text-xs text-gray-500 mt-2">Recibirás notificaciones cuando lleguen nuevos leads o emails</p>
          </CardContent>
        </Card>

        {/* Gemini */}
        <Card className="dark:bg-gray-900 dark:border-gray-800">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-gray-900 dark:text-white">
              <Bot className="w-5 h-5" />
              Gemini AI (Inteligencia Artificial)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Label>API Key de Gemini</Label>
            <Input 
              type="password"
              value={geminiApiKey} 
              onChange={(e) => setGeminiApiKey(e.target.value)} 
              placeholder="AIza..." 
            />
            <p className="text-xs text-gray-500 mt-2">Obtén tu API key en <a href="https://aistudio.google.com/apikey" target="_blank" className="text-indigo-500 hover:underline">Google AI Studio</a></p>
          </CardContent>
        </Card>

        {/* Sync */}
        <Card className="dark:bg-gray-900 dark:border-gray-800">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-gray-900 dark:text-white">
              <RefreshCw className="w-5 h-5" />
              Sincronización Manual
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-gray-500">Sincroniza manualmente los emails de los últimos 30 días.</p>

            {syncResult && (
              <Alert className={syncResult.success ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}>
                {syncResult.success ? <CheckCircle className="h-4 w-4 text-green-600" /> : <XCircle className="h-4 w-4 text-red-600" />}
                <AlertDescription className={syncResult.success ? "text-green-800" : "text-red-800"}>
                  {syncResult.message}
                </AlertDescription>
              </Alert>
            )}

            <Button onClick={handleSyncEmails} disabled={isSyncing} className="bg-indigo-600 hover:bg-indigo-700">
              {isSyncing ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              {isSyncing ? "Sincronizando..." : "Sincronizar Emails"}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}